// Group 1 project write-ups. Real McKay jobs, rewritten in-house voice.
// Every image below is an asset that already exists in this project.

import type { BlogPost } from "./blogPosts";

import heroCityHeights from "@/assets/blog/blog-4.jpg.asset.json";
import sectionCityHeights from "@/assets/mckay/roof-replacement.webp.asset.json";
import heroOceanBeach from "@/assets/blog/blog-3.jpg.asset.json";
import sectionOceanBeach from "@/assets/service-heroes/flat-roof-repair.jpg";
import heroTorchDown from "@/assets/blog/blog-2.jpg.asset.json";
import sectionTorchDown from "@/assets/service-heroes/torch-down-roofing.jpg.asset.json";

export const group1Posts: BlogPost[] = [
  {
    slug: "city-heights-roof-replacement-45th-street-san-diego",
    title: "City Heights Roof Replacement on 45th Street | McKay",
    h1: "How we replaced two aging shingle roofs on 45th Street in City Heights",
    metaDescription:
      "A full tear-off roof replacement on two units at 4182 & 4184 45th Street in City Heights, San Diego: what we found under the old shingles and what went back on.",
    keywords:
      "City Heights roof replacement, 45th Street San Diego roofing, 92105 roof replacement, asphalt shingle roof San Diego, San Diego roofing contractor",
    category: "Projects",
    datePublished: "2026-04-02",
    dateModified: "2026-09-09",
    readMinutes: 8,
    hero: {
      src: heroCityHeights.url,
      alt: "Completed asphalt shingle roof replacement on a duplex in City Heights, San Diego",
      caption: "Both units at 4182 & 4184 45th Street got a full tear-off and a brand new shingle roofing system.",
    },
    intro: [
      "Some jobs are memorable just because of how much the property changes by the end of the day. This one, a roof replacement on 45th Street in City Heights, was one of those. The two units on this property had been carrying the same worn-out asphalt shingle roof for years, and by the time we got the call, patching it any further would have just been throwing money at a roof that was already done.",
      "The address is 4182 & 4184 45th Street, right in the 92105 zip code, one of the older pockets of San Diego where a lot of the housing stock is original construction with decades of past repairs layered on top. That history matters when you are quoting a replacement, because you rarely know what is actually under the shingles until you pull them off.",
      "This post walks through what we found once we opened the roof up, why we went with a full tear-off instead of a layover, and what a proper shingle roofing system looks like when it is built the right way instead of patched together one more time.",
    ],
    tldr: [
      "Both units at 4182 & 4184 45th Street had asphalt shingles well past their service life, with visible curling and granule loss.",
      "We did a complete tear-off down to the decking, which is the only way to catch hidden wood damage on an older City Heights home.",
      "Decking repairs were needed in several spots before the new underlayment, flashing, and shingles went on.",
      "The finished roof gives both units a sealed, properly ventilated system built for San Diego's constant sun exposure, not just a fresh coat of shingles.",
    ],
    sections: [
      {
        id: "why-replacement-not-repair",
        h2: "Why was replacement the right call instead of another repair?",
        blocks: [
          {
            type: "p",
            text: "The shingles on this property were old asphalt three-tab, and they had reached the point where every part of the system was failing at once, not just one spot. That is the line we look for. A single bad flashing detail or a cracked vent boot is a repair. Shingles that are worn thin across both units, curling at the edges, and starting to fail in multiple places is a replacement conversation.",
          },
          {
            type: "p",
            text: "We get this question a lot on older San Diego homes: can you just patch the bad section and leave the rest? Sometimes, yes. On this one, no. The wear was spread across both roof planes, and patching one corner would have left the homeowner paying for a new problem within a year or two. If you are not sure which side of that line your own roof is on, our [roof inspection](/services/roof-inspection) service is built to answer exactly that question before you spend a dollar.",
          },
          {
            type: "callout",
            title: "The tell on this roof",
            text: "Shingles that are worn down, curling, and already failing in more than one spot are not a repair candidate anymore. That pattern almost always means the whole system has aged out together.",
          },
        ],
        image: {
          src: sectionCityHeights.url,
          alt: "Worn asphalt shingles before a City Heights roof replacement",
          caption: "Years of San Diego sun had left the original shingles thin, curling, and past the point of a spot repair.",
        },
      },
      {
        id: "full-tear-off",
        h2: "What does a full tear-off actually involve, and why not just layer over it?",
        blocks: [
          {
            type: "p",
            text: "We started this job with a complete tear-off of the existing shingles, all the way down to the decking. That is not the fast way to do a re-roof, and it is not the cheap way either, but it is the only way to actually see what you are building on top of.",
          },
          { type: "h3", text: "What a full tear-off lets us do" },
          {
            type: "ul",
            items: [
              "Catch hidden decking damage before it becomes a structural repair.",
              "Make wood repairs where the sheathing has softened, instead of nailing new shingles into it.",
              "Install underlayment and flashing correctly, on a clean, flat surface.",
              "Build a roof that is actually rated to last, rather than one hiding a weak base under a new layer.",
            ],
          },
          {
            type: "p",
            text: "Once the old shingles came off, we found sections of decking that needed to be replaced, which is very common on older City Heights properties. A lot of the time, that damage is invisible from above or from inside the attic until the roof is fully opened up. That is exactly why we do not skip this step and do not offer to install over old shingles just to save a day of labor.",
          },
        ],
        image: {
          src: heroCityHeights.url,
          alt: "Roof decking exposed during a full tear-off roof replacement in City Heights",
          caption: "Once the shingles were off, we could see and repair the wood decking, not just guess at its condition.",
        },
      },
      {
        id: "decking-repairs",
        h2: "What kind of decking damage shows up on older City Heights homes?",
        blocks: [
          {
            type: "p",
            text: "This property is a good example of what we see across a lot of the 92105 area. Homes here tend to be original construction with multiple past roofing jobs layered over the decades, and that history often includes shortcuts. A previous repair or install that skipped proper flashing, or that trapped moisture instead of sealing it out, eventually shows up as soft or damaged wood underneath.",
          },
          {
            type: "ol",
            items: [
              "We inspected the entire deck surface after tear-off, checking for soft spots, delamination, and water staining.",
              "Damaged sections were cut out and replaced with new sheathing, matched and fastened to code.",
              "We rechecked every repaired section before laying underlayment, since covering a bad patch is worse than finding it.",
            ],
          },
          {
            type: "quote",
            text: "You won't always see decking problems until the roof is fully opened up. That's exactly why we don't skip this step.",
            cite: "McKay field notes from the 45th Street job",
          },
        ],
        image: {
          src: sectionCityHeights.url,
          alt: "New wood decking installed during a San Diego roof replacement",
          caption: "Soft or damaged sheathing gets replaced before anything else goes on the roof.",
        },
      },
      {
        id: "new-shingle-system",
        h2: "What went into the new shingle roofing system?",
        blocks: [
          {
            type: "p",
            text: "Once the decking was solid, we installed a complete asphalt shingle roofing system across both units. Roofing in City Heights, like most of San Diego, means dealing with near-constant sun exposure, and that sun is what dries out and weakens shingles over time. Good installation matters just as much as the shingle brand itself when it comes to how long a roof actually lasts.",
          },
          { type: "h3", text: "What we installed on this job" },
          {
            type: "ul",
            items: [
              "New roofing underlayment across the full deck.",
              "Flashing at every vent and roof penetration.",
              "New asphalt shingles across both units, laid with straight, consistent courses.",
              "Ridge components to keep proper attic ventilation, which helps the new shingles hold up longer.",
            ],
          },
          {
            type: "p",
            text: "Each of those layers does a specific job, and skipping any one of them is how a roof that looks fine on day one starts leaking in year three. For homeowners weighing this same decision, our [roof replacement](/services/roof-replacement) page breaks down what a full system install includes, and the [NRCA](https://www.nrca.net/) publishes homeowner-facing guidance on what a properly built shingle roof assembly should include.",
          },
        ],
        image: {
          src: heroCityHeights.url,
          alt: "New asphalt shingles installed across a City Heights duplex roof",
          caption: "Straight shingle lines and sealed flashing are what separate a proper install from a quick patch job.",
        },
      },
      {
        id: "common-city-heights-problems",
        h2: "What roofing problems come up most often in the 92105 area?",
        blocks: [
          {
            type: "p",
            text: "A lot of the roofs we replace in City Heights show the same patterns, which is part of why we know what to expect before we even get on the ladder.",
          },
          {
            type: "ul",
            items: [
              "Worn-out or curling shingles from years of direct sun exposure.",
              "Small leaks that go unnoticed until they spread across a wider area.",
              "Soft or damaged decking hiding underneath an intact-looking surface.",
              "Poor attic ventilation left over from older installs, which shortens the life of whatever roof goes on next.",
            ],
          },
          {
            type: "p",
            text: "Because of San Diego's climate, roofs in this part of the city tend to age faster than a lot of homeowners expect. A small, ignored issue can turn into a full tear-off if it sits long enough, which is why catching it early with a [roof repair](/services/roof-repair) visit is almost always cheaper than waiting.",
          },
        ],
        image: {
          src: sectionCityHeights.url,
          alt: "Older asphalt shingle roofline typical of homes in City Heights, San Diego",
          caption: "Older, layered roofing histories are common across City Heights, which makes a full tear-off the safer call more often than not.",
        },
      },
      {
        id: "finished-result",
        h2: "What did the finished roof look like on move-out day?",
        blocks: [
          {
            type: "p",
            text: "By the time we wrapped up, both units at 4182 & 4184 45th Street had brand new shingle roofs installed from the decking up. That is exactly the outcome we aim for on every project.",
          },
          {
            type: "ul",
            items: [
              "Clean, straight shingle lines across both roof planes.",
              "A properly sealed roofing system, including flashing and ridge ventilation.",
              "Strong long-term durability built on repaired, solid decking.",
              "A noticeable visual upgrade to the whole property.",
            ],
          },
          {
            type: "p",
            text: "You can genuinely see the difference between where this roof started and where it ended up, and that transformation is a big part of why these are the jobs we like sharing. If you want to see more finished work like this, our [project gallery](/gallery) has photos from similar replacements around the county.",
          },
        ],
        image: {
          src: heroCityHeights.url,
          alt: "Finished roof replacement viewed from the street in City Heights, San Diego",
          caption: "A clean, sealed shingle roof from the street, and a solid, repaired deck underneath it that nobody sees.",
        },
      },
      {
        id: "city-heights-vs-other-areas",
        h2: "How is roofing in City Heights different from newer San Diego neighborhoods?",
        blocks: [
          {
            type: "p",
            text: "City Heights is a unique part of San Diego when it comes to roofing work. A lot of the homes are older, which usually means original structures, multiple past repairs, and roofing systems that were layered over each other across decades instead of properly replaced.",
          },
          {
            type: "p",
            text: "Compared with newer areas of the county, roofs here typically need more detailed inspection and repair work during a replacement. That is exactly why having a crew that takes the time to open the roof up and rebuild it properly, rather than rushing to lay new shingles over old problems, matters more here than it might elsewhere. We work across the wider region too, and our [areas served](/areas-served) page has the full list of where we take on projects like this one.",
          },
        ],
        image: {
          src: sectionCityHeights.url,
          alt: "McKay Roofing crew working on a City Heights, San Diego roof replacement",
          caption: "Older neighborhoods reward a crew willing to slow down and inspect, not just install.",
        },
      },
    ],
    faqs: [
      {
        q: "Why did both units need a full tear-off instead of a partial repair?",
        a: "The shingles across both roof planes were worn, curling, and failing in multiple spots at once. That kind of widespread wear means a repair would only buy a short amount of time before new leaks showed up somewhere else on the same roof.",
      },
      {
        q: "How common is decking damage on older City Heights homes?",
        a: "Very common. A lot of these properties have original construction and multiple past roofing layers, and that history often hides moisture damage that only shows up once the shingles are fully removed.",
      },
      {
        q: "How long does a two-unit roof replacement like this take?",
        a: "A job of this size, covering two adjoining units with decking repairs included, typically runs three to five days depending on how much sheathing needs to be replaced and the weather window.",
      },
      {
        q: "Does San Diego's climate really age shingle roofs faster?",
        a: "Yes. Constant sun exposure dries out and weakens asphalt shingles over time, even without heavy rain. That is why roofs in sun-exposed neighborhoods like City Heights often need replacement sooner than the shingle's rated lifespan suggests.",
      },
      {
        q: "What should I do if I notice small leaks or missing shingles on an older roof?",
        a: "Get it looked at before it spreads. A small, isolated issue is usually a straightforward repair, but the same issue left alone for a year or two can turn into the kind of full tear-off this property needed.",
      },
    ],
    cta: {
      title: "Got an older roof in City Heights or nearby?",
      text: "We will walk the roof, tell you honestly whether it is a repair or a replacement, and put both numbers in writing before you decide anything.",
    },
    ctaType: "roofing",
  },
  {
    slug: "ocean-beach-flat-roof-replacement-bacon-street-san-diego",
    title: "Ocean Beach Flat Roof Replacement, Bacon Street | McKay",
    h1: "Replacing a failing flat roof near Bacon Street in Ocean Beach",
    metaDescription:
      "A full tear-off flat roof replacement near Bacon Street in Ocean Beach, San Diego: the failing cap sheet we found, the torch-down system we installed, and why.",
    keywords:
      "Ocean Beach flat roof replacement, Bacon Street San Diego roofing, torch down roof Ocean Beach, 92107 flat roof, coastal roof replacement San Diego",
    category: "Projects",
    datePublished: "2026-04-16",
    dateModified: "2026-09-09",
    readMinutes: 8,
    hero: {
      src: heroOceanBeach.url,
      alt: "Newly installed flat roof torch-down system on a home near Bacon Street in Ocean Beach, San Diego",
      caption: "A full tear-off and new torch-down system replaced an aging cap sheet roof a few blocks from the sand.",
    },
    intro: [
      "We recently wrapped up a flat roof replacement out in Ocean Beach, right off Bacon Street, and it is one of those jobs that feels good to look back on once it is finished. If you know OB, you know the setting: close to the water, salty air, sun for most of the day, and a mix of older homes and rentals that have taken a beating over the years. Flat roofs out here simply do not age the same way inland roofs do.",
      "This particular roof had an older cap sheet system that was completely done. The seams were opening up, the surface was worn through in places, and it had already been patched more than once. At a certain point you stop spending money on repairs and just replace the thing properly, and that is exactly the conversation we had with the homeowner here.",
      "Below is what we actually found once we got up there, why a full tear-off was the right call instead of another patch, and what went into the new torch-down system that is now protecting the property.",
    ],
    tldr: [
      "The existing cap sheet near Bacon Street had separating seams, worn surface areas, and a history of prior patch repairs.",
      "We did a complete tear-off to the decking and found damaged sheathing underneath, which is common on older Ocean Beach flat roofs.",
      "The new roof is a torch-down modified bitumen system, chosen for its resistance to UV, heat, and coastal moisture.",
      "Flat roofs in Ocean Beach and along the coast tend to fail faster than inland roofs because of constant sun, salt air, and slower drainage.",
    ],
    sections: [
      {
        id: "what-we-found",
        h2: "What did we actually find on this Bacon Street roof?",
        blocks: [
          {
            type: "p",
            text: "When we got up there, it was clear right away that this was not just a cosmetic issue. The seams in the old cap sheet were starting to separate, there were visibly weak spots across the surface, and you could feel areas underfoot that were not solid anymore. That combination is pretty typical for older flat roofs in Ocean Beach, especially around Bacon Street, Newport Ave, and down toward Sunset Cliffs, where the marine layer rolls in most mornings.",
          },
          {
            type: "p",
            text: "Homeowners ask us the same three questions on almost every one of these calls: can you just patch it, do I really need a full replacement, and how much longer can I get out of this roof? The honest answer is that sometimes a patch is the right move. But once a system starts failing across multiple areas the way this one was, patching becomes a short-term band-aid rather than a real fix. If you are seeing multiple problem spots on your own flat roof, it is usually time to start thinking about a [roof replacement](/services/roof-replacement) instead of another round of patches.",
          },
          {
            type: "callout",
            title: "When patching stops making sense",
            text: "A single soft spot or one open seam is often repairable. Seams separating in several places, plus a history of past patches, means the whole membrane has aged out together.",
          },
        ],
        image: {
          src: sectionOceanBeach,
          alt: "Worn and separating cap sheet seams on an Ocean Beach flat roof before replacement",
          caption: "Separating seams and soft spots across multiple areas meant patching was no longer a real option.",
        },
      },
      {
        id: "full-tear-off",
        h2: "Why did we tear off the whole cap sheet instead of going over it?",
        blocks: [
          {
            type: "p",
            text: "We went with a full tear-off on this one. No going over the existing system, no shortcuts. Everything came off so we could get down to the decking and see exactly what condition it was actually in, not just what it looked like from the surface.",
          },
          { type: "h3", text: "Why this step matters on a flat roof" },
          {
            type: "ul",
            items: [
              "Flat roofs hide problems well. From the top, a section can look fine while the decking beneath it is already compromised.",
              "Moisture sits longer on flat surfaces than on a pitched roof, which means rot and soft decking are more common than most homeowners expect.",
              "Layering a new membrane over a weak deck just builds a new roof on a bad foundation, which is how systems fail early.",
            ],
          },
          {
            type: "p",
            text: "As soon as we opened this one up, we found areas of damaged decking underneath the old cap sheet, which was not surprising given the roof's age and location. We removed the bad sections, reinforced what needed it, and confirmed everything was solid before installing anything new on top.",
          },
        ],
        image: {
          src: heroOceanBeach.url,
          alt: "Exposed roof decking during a flat roof tear-off in Ocean Beach, San Diego",
          caption: "Tearing off the entire cap sheet let us find and repair damaged decking before it became a bigger problem.",
        },
      },
      {
        id: "new-torch-down-system",
        h2: "Why did we choose a torch-down system for this flat roof?",
        blocks: [
          {
            type: "p",
            text: "Once the decking was prepped and repaired, we installed a brand new torch-down roofing system. For flat roofs in this part of San Diego, torch-down is one of the strongest options available. It forms a fully sealed membrane that holds up well against constant sun exposure, summer heat, and the coastal moisture that comes with living a few blocks from the ocean.",
          },
          {
            type: "p",
            text: "We made sure every seam was clean, properly bonded, and sealed tight before calling the job finished. That detail work at the seams and transitions is really what separates a torch-down roof that lasts fifteen or twenty years from one that starts giving problems again within a couple of seasons. For more on how this material compares with other flat roof options, our [torch-down roofing](/services/torch-down-roofing) page covers the system in more detail, and manufacturer specs for modified bitumen systems are published by suppliers like [GAF](https://www.gaf.com/en-us/roofing-products/commercial-roofing-products/modified-bitumen).",
          },
        ],
        image: {
          src: sectionOceanBeach,
          alt: "New torch-down roofing membrane installed on a flat roof near the coast in San Diego",
          caption: "A sealed, fully bonded torch-down membrane replaced the old cap sheet system.",
        },
      },
      {
        id: "why-ob-roofs-fail-faster",
        h2: "Why do flat roofs in Ocean Beach fail faster than inland roofs?",
        blocks: [
          {
            type: "p",
            text: "This is something we see constantly, especially throughout the 92107 area. Ocean Beach roofs deal with a combination of conditions that inland San Diego roofs simply do not face to the same degree.",
          },
          {
            type: "ul",
            items: [
              "**Constant UV exposure** from long, mostly clear days near the coast.",
              "**Salt in the air** coming off the ocean, which attacks fasteners and seams faster than inland climates.",
              "**Cooler nights** that create condensation cycles underneath the membrane.",
              "**Flat surfaces** where water does not run off as quickly as it would on a pitched roof.",
            ],
          },
          {
            type: "p",
            text: "A lot of people assume a roof should last 25 to 30 years no matter where it sits, but that is not always realistic in a coastal environment like OB. Maintenance and proper installation matter far more here than the material's rated lifespan on a spec sheet. The [Energy.gov](https://www.energy.gov/energysaver/roofing) resources on roofing materials cover some of this UV and heat exposure math in more general terms, but coastal salt exposure is a factor we mainly track through our own field history.",
          },
        ],
        image: {
          src: heroOceanBeach.url,
          alt: "Coastal flat roof in Ocean Beach exposed to sun and salt air",
          caption: "Sun, salt air, and slow drainage all shorten a flat roof's real-world lifespan along the coast.",
        },
      },
      {
        id: "working-in-ocean-beach",
        h2: "What is it like doing roofing work throughout Ocean Beach?",
        blocks: [
          {
            type: "p",
            text: "We do a lot of work around Ocean Beach, Point Loma, and Mission Beach, and every job out here has a bit of its own personality. You get tight streets, older builds, a real mix of roof types on the same block, and homeowners who genuinely care about their property, whether they live in it or rent it out.",
          },
          {
            type: "p",
            text: "It is not cookie-cutter work, and this Bacon Street project was a good example of that. Not a massive job in square footage, but one that needed to be done right the first time. Finishing up and looking out over OB from a clean, solid roof is a good feeling every time. If you are curious what other coastal roofing jobs around the region look like, our [project gallery](/gallery) has more examples.",
          },
        ],
        image: {
          src: sectionOceanBeach,
          alt: "Finished flat roof project near Bacon Street in Ocean Beach, San Diego",
          caption: "A modest job in square footage, but one worth doing properly given the coastal conditions.",
        },
      },
      {
        id: "who-we-work-with",
        h2: "Who does McKay handle flat roof replacements for in Ocean Beach?",
        blocks: [
          {
            type: "p",
            text: "At McKay Roofing & Solar, we handle flat roof replacement projects throughout Ocean Beach and greater San Diego. That includes homeowners living in their properties, rental property owners, property managers overseeing multiple units, and investors fixing up older buildings before resale.",
          },
          {
            type: "p",
            text: "We keep the process simple no matter who we are working with. We tell you what is actually going on with the roof, what your real options are, and what makes sense long term instead of overselling a bigger job than the roof needs. You can see the wider area we cover on our [areas served](/areas-served) page.",
          },
        ],
        image: {
          src: heroOceanBeach.url,
          alt: "McKay Roofing crew completing a flat roof replacement in Ocean Beach, San Diego",
          caption: "Straightforward answers and a properly sealed roof, whether the client lives there or rents it out.",
        },
      },
      {
        id: "schedule-inspection",
        h2: "What should you do if your flat roof is showing similar signs?",
        blocks: [
          {
            type: "p",
            text: "If your flat roof is starting to crack, leak, or just looks worn out the way this one did, it is worth getting ahead of it rather than waiting for a bigger failure. We will come take a look, walk the roof with you if you want to see it firsthand, and give you a straight answer on whether it needs a repair or a full replacement.",
          },
          {
            type: "p",
            text: "Not sure where your own roof stands? A quick [roof inspection](/services/roof-inspection) is the fastest way to find out before a small issue turns into interior damage, and you can reach our team directly through the [contact page](/contact) to get a visit scheduled.",
          },
        ],
        image: {
          src: sectionOceanBeach,
          alt: "Homeowner reviewing a flat roof inspection near Bacon Street in Ocean Beach",
          caption: "A walk-through inspection before the rainy season is cheaper than an emergency call during it.",
        },
      },
    ],
    faqs: [
      {
        q: "Could this roof have been patched instead of fully replaced?",
        a: "Not really, at this stage. The seams were separating in multiple places and the surface had soft spots across several areas, not just one. Patching a roof failing in that many places usually buys only a few months before a new leak shows up.",
      },
      {
        q: "Why is torch-down a good choice for flat roofs in Ocean Beach?",
        a: "Torch-down forms a fully sealed, bonded membrane that resists UV damage, heat, and the coastal moisture that comes with being close to the ocean. That combination makes it one of the more durable options for flat sections in a marine climate.",
      },
      {
        q: "How long does a flat roof replacement like this usually take?",
        a: "A tear-off and torch-down install on a roof this size typically takes two to four days, depending on how much decking needs to be replaced and whether weather cooperates.",
      },
      {
        q: "Do flat roofs near the coast really need replacement sooner than inland roofs?",
        a: "Generally yes. Salt air, constant sun, and slower drainage all speed up wear on flat roofs in areas like Ocean Beach compared with similar roofs further inland.",
      },
      {
        q: "What is the first sign that a flat roof needs more than a patch?",
        a: "Seams separating in more than one location, soft spots underfoot in multiple areas, or a roof that has already been patched two or more times are all signs the whole system has aged out together.",
      },
    ],
    cta: {
      title: "Dealing with an aging flat roof near the coast?",
      text: "We will inspect it, explain exactly what we find, and give you an honest answer on repair versus replacement, no upsell required.",
    },
    ctaType: "roofing",
  },
  {
    slug: "torch-down-roof-installation-san-diego",
    title: "Torch-Down Roof Installation in San Diego | McKay",
    h1: "Torch-down roof installation in San Diego: tear-off and a 20-year system",
    metaDescription:
      "A full tear-off and torch-down roof installation in San Diego, replacing a failing cap sheet with a new 20-year manufacturer-backed modified bitumen system.",
    keywords:
      "torch down roof installation San Diego, torch down roofing, modified bitumen roof San Diego, flat roof replacement San Diego, 20 year roof warranty",
    category: "Projects",
    datePublished: "2026-04-30",
    dateModified: "2026-09-09",
    readMinutes: 8,
    hero: {
      src: heroTorchDown.url,
      alt: "New torch-down modified bitumen roof installed on a flat roof section in San Diego",
      caption: "A full tear-off and a new 20-year torch-down system replaced a failing cap sheet on this San Diego flat roof.",
    },
    intro: [
      "If a flat roof is starting to crack, blister, or leak, it is usually not something you can patch forever. At some point the old cap sheet has to come off and a new system needs to go on properly. We recently completed exactly that: a full tear-off and torch-down roof installation in San Diego, replacing an aging cap sheet system with a new 20-year manufacturer-backed roofing system.",
      "This was a smaller project in terms of square footage, a 3-square roof section, roughly 300 square feet, but the work involved was the same as any larger flat roof replacement. The existing cap sheet had reached the end of its useful life, and the homeowner wanted a system that would actually hold up rather than another short-term fix.",
      "Below is exactly what the job included, why the tear-off was necessary rather than optional, and why torch-down roofing continues to be one of our go-to recommendations for flat and low-slope sections across the county.",
    ],
    tldr: [
      "This was a 3-square (about 300 square foot) torch-down installation with a complete tear-off of the old cap sheet.",
      "The new system carries a 20-year manufacturer warranty when installed to spec.",
      "Cracking, brittleness, and moisture intrusion in the old cap sheet made a tear-off necessary instead of a layover.",
      "Torch-down roofing holds up well in San Diego because it resists UV damage, handles heat, and seals fully at seams and penetrations.",
    ],
    sections: [
      {
        id: "what-the-job-included",
        h2: "What did this torch-down roof installation actually include?",
        blocks: [
          {
            type: "p",
            text: "This project covered a 3-square roof section, approximately 300 square feet, where the existing cap sheet had reached the end of its life. It was a contained job, but every step still had to be done in the right order for the new system to perform the way it is supposed to.",
          },
          {
            type: "ol",
            items: [
              "Complete tear-off of the old cap sheet roofing system.",
              "Surface prep and inspection of the roof deck once it was exposed.",
              "Installation of a new torch-down modified bitumen system.",
              "Proper sealing around all edges and penetrations.",
              "Final inspection to confirm long-term durability before calling it done.",
            ],
          },
          {
            type: "p",
            text: "Torch-down systems are one of the most reliable options for flat and low-slope roofs in San Diego because they hold up well against heat, direct sun exposure, and standing water, three things that break down cheaper roll roofing materials fast. Our [flat roof repair](/services/flat-roof-repair) and [torch-down roofing](/services/torch-down-roofing) pages cover both the smaller repair side and the full install side of this same work.",
          },
        ],
        image: {
          src: sectionTorchDown.url,
          alt: "Torch-down modified bitumen roofing system being installed on a San Diego flat roof section",
          caption: "A contained 3-square section, but every layer, from tear-off to sealed edges, still had to be done right.",
        },
      },
      {
        id: "why-tear-off-was-necessary",
        h2: "Why was a full tear-off necessary instead of adding another layer?",
        blocks: [
          {
            type: "p",
            text: "A lot of homeowners ask if we can just add another layer on top of what's already there. In some cases, that can work. Not on this roof, though, because the existing system was already failing.",
          },
          { type: "h3", text: "What we found on the old cap sheet" },
          {
            type: "ul",
            items: [
              "Cracking and brittleness across the surface of the material.",
              "Signs of moisture already getting underneath the cap sheet.",
              "Worn areas caused by long-term, unrelenting sun exposure.",
            ],
          },
          {
            type: "p",
            text: "When a roof is already showing that pattern, layering a new membrane on top just traps the existing problems underneath a fresh surface. It looks fine for a while, then fails from below. A full tear-off gives you a clean start and a system that actually lasts as long as it is designed to.",
          },
          {
            type: "callout",
            title: "The layover trap",
            text: "Installing new roofing over a cracked, moisture-compromised cap sheet does not fix anything. It just hides the failure for a season or two before it reappears, usually as a harder repair than it would have been the first time.",
          },
        ],
        image: {
          src: heroTorchDown.url,
          alt: "Cracked and brittle cap sheet roofing before torch-down replacement in San Diego",
          caption: "Cracking, brittleness, and trapped moisture meant a tear-off was the only real option here.",
        },
      },
      {
        id: "why-torch-down-works-here",
        h2: "Why does torch-down roofing work so well in San Diego's climate?",
        blocks: [
          {
            type: "p",
            text: "San Diego's climate is genuinely tough on flat roofs. Constant sun exposure breaks down cheaper roofing materials fast, even in a coastal climate that looks mild on paper. Torch-down roofing is designed specifically for this kind of environment.",
          },
          {
            type: "ul",
            items: [
              "It creates a fully sealed, watertight surface across the entire roof plane.",
              "It resists UV damage better than basic roll roofing or lower-grade cap sheet systems.",
              "It handles daily and seasonal temperature swings without cracking.",
              "It lasts significantly longer when it is installed correctly, which comes down to seam bonding and edge detail.",
            ],
          },
          {
            type: "p",
            text: "For flat or low-slope roof sections, it remains one of the best long-term options available. The [NRCA](https://www.nrca.net/) publishes technical guidance on modified bitumen systems for contractors and homeowners who want to understand the material in more depth, and our own [roof replacement](/services/roof-replacement) page covers how flat systems compare with pitched roofing in terms of lifespan and cost.",
          },
        ],
        image: {
          src: sectionTorchDown.url,
          alt: "Sealed torch-down roof surface resisting sun exposure on a San Diego home",
          caption: "A properly bonded torch-down surface handles San Diego's sun and heat far better than basic roll roofing.",
        },
      },
      {
        id: "20-year-warranty",
        h2: "What does the 20-year manufacturer warranty actually cover?",
        blocks: [
          {
            type: "p",
            text: "This installation includes a 20-year manufacturer warranty, which covers the roofing material itself when it is installed to the manufacturer's specifications. That last part matters more than most homeowners realize.",
          },
          {
            type: "ul",
            items: [
              "The system is installed to manufacturer standards, not shortcuts that void the coverage later.",
              "The materials themselves are designed and rated for long-term performance.",
              "You are not relying on short-term patch fixes to get another season out of a failing roof.",
            ],
          },
          {
            type: "p",
            text: "That warranty is really the difference between a temporary repair and a real, long-term solution. Manufacturer warranty terms for modified bitumen systems are typically published directly by suppliers such as [GAF](https://www.gaf.com/en-us/roofing-products/commercial-roofing-products/modified-bitumen), and we walk every client through what their specific warranty covers before the job starts, not after.",
          },
        ],
        image: {
          src: heroTorchDown.url,
          alt: "Finished torch-down roof with manufacturer warranty documentation for a San Diego project",
          caption: "A 20-year warranty only holds up if the install itself meets manufacturer spec, which is why sealing detail matters.",
        },
      },
      {
        id: "signs-you-need-replacement",
        h2: "How do you know if your own flat roof needs replacement?",
        blocks: [
          {
            type: "p",
            text: "If you are not sure whether your flat roof needs a repair or a full replacement, there are a handful of signs worth checking for before deciding either way.",
          },
          {
            type: "ul",
            items: [
              "Recurring leaks that keep showing up in the same spot no matter how many times it has been patched.",
              "Visible cracks or bubbling across the surface of the cap sheet.",
              "Soft spots underfoot when you walk the roof.",
              "Pooled water that never fully drains, even a day or two after rain.",
              "A roof that is already 10 to 15 years old or older.",
            ],
          },
          {
            type: "p",
            text: "If you are seeing any of those, it is worth getting the roof inspected before it turns into interior water damage. A [roof inspection](/services/roof-inspection) is a quick, low-cost way to get a real answer instead of guessing.",
          },
        ],
        image: {
          src: sectionTorchDown.url,
          alt: "Pooled water and worn surface on an aging San Diego flat roof",
          caption: "Pooling water and surface bubbling are two of the clearest signs a flat roof is due for replacement.",
        },
      },
      {
        id: "details-that-matter",
        h2: "What separates a good torch-down install from a mediocre one?",
        blocks: [
          {
            type: "p",
            text: "Not all torch-down installations are the same, even when the material is identical. The details are what actually determine how long the roof lasts, especially the sealing at edges, penetrations, and transitions between roof planes.",
          },
          {
            type: "p",
            text: "Because we handle both roofing and solar work, we also make sure a flat roof like this is ready for anything that might go on top of it down the road, whether that is solar mounting hardware or HVAC equipment. Building that consideration in from the start avoids having to cut into a brand-new roof a few years later. See our [solar-ready roofing](/services/roof-replacement) guidance or reach out through our [contact page](/contact) if that is part of your own plans.",
          },
        ],
        image: {
          src: heroTorchDown.url,
          alt: "Detail work at roof edges and penetrations on a finished torch-down installation in San Diego",
          caption: "Sealed edges and clean penetration details are what separate a roof that lasts twenty years from one that doesn't.",
        },
      },
    ],
    faqs: [
      {
        q: "How big was this torch-down roofing project?",
        a: "This particular job covered a 3-square section, roughly 300 square feet, but included a full tear-off, deck prep, and installation of a complete new torch-down system despite the smaller footprint.",
      },
      {
        q: "Why couldn't the old cap sheet just be covered with a new layer?",
        a: "It was already cracking, brittle, and letting moisture in underneath. Covering that with a new layer would have trapped the existing problems and led to a bigger failure later instead of solving anything.",
      },
      {
        q: "What does the 20-year manufacturer warranty actually require?",
        a: "The warranty applies when the system is installed exactly to the manufacturer's specifications, which is why installation quality, not just material choice, determines whether that coverage is worth anything.",
      },
      {
        q: "Is torch-down roofing a good fit for small flat roof sections, or only large ones?",
        a: "It works well at almost any size. The key factors are proper tear-off, solid decking, and correctly sealed seams and edges, all of which apply the same way whether the section is 300 square feet or several thousand.",
      },
      {
        q: "How do I know if my flat roof needs a full replacement rather than a repair?",
        a: "Recurring leaks in the same spot, visible cracking or bubbling, soft spots underfoot, standing water that will not drain, and a roof already past 10 to 15 years old are all signs it is time for a real inspection.",
      },
      {
        q: "Does McKay handle both the roofing and solar side of a flat roof project?",
        a: "Yes. We install both, which means we plan flat roof replacements with future solar mounting or equipment loads in mind so the roof does not need to be cut into again down the road.",
      },
    ],
    cta: {
      title: "Need a flat roof inspection or a torch-down quote?",
      text: "Get a free, no-pressure estimate and we will walk you through whether your roof needs a repair or a full torch-down replacement.",
    },
    ctaType: "roofing",
  },
];
